/**
 * gasOverlay.ts — Per-enemy gas damage visual overlay
 * =====================================================================
 * BETA 16.22 — Per Dylan: "[gas cloud sprites] need to be at 25% opacity
 * and activate when gas damage occurs on any and all buncha bullshit
 * items and any and all wepons with gas damage added. this sprite
 * should live over enemies but under the main bot or character playing
 * and under weapons floating. and projectiles. these sprites should be
 * bigger dependin on enemy it hits to be the size of their hit box. i
 * need text to be avoe to show the gas damage or slow or whatever. if
 * the enemy it getting gas dammage for many seconds it needs to tick
 * that gas dammage over the enemy over and over in real time of the
 * effect."
 *
 * Module owns a per-enemy gas state map. When notifyEnemyGasTick fires
 * (called from enemyAdapter when a gas-tagged AoE damage request hits
 * an enemy) it records the tick + spawns a floating "+N gas" damage
 * number above the enemy via the existing weaponSystem damageNumbers
 * pipeline. State expires GAS_FADE_TIME_SEC after the last tick — so
 * the cloud overlay only renders while the enemy is actively being
 * gassed, naturally fading out when they leave the cloud.
 *
 * Layer ordering (matches Dylan's spec):
 *   - renderGasOverlayBelow: drawn BELOW the enemy sprite (so the
 *     cloud sits "over enemies" the same way a backlit halo would —
 *     this is the "live over enemies" layer; reads visually as gas
 *     surrounding the enemy).
 *   - renderGasOverlayAbove (currently unused): reserved if we ever
 *     want a top-pass cloud, e.g. for elite/boss visual emphasis.
 *
 * Enemies render between these two passes via enemiesModule.render.
 * Player + weapons + projectiles all render AFTER, so the spec's
 * "under bot/character/weapons/projectiles" requirement is met by
 * the existing render order — no per-pass alpha-juggling required.
 * =====================================================================
 */

const GAS_FADE_TIME_SEC = 1.2;          // overlay lingers 1.2s after last tick
const GAS_OVERLAY_OPACITY = 0.25;        // Dylan: "25% opacity"
const GAS_TEXT_FLOAT_DURATION = 0.9;     // damage tick number floats up for 0.9s

export const GAS_SOURCE_IDS: ReadonlySet<string> = new Set([
  'R11',  // Diarrhea Gas item
  // Add any future gas-tagged item / weapon source IDs here.
  // Convention: any sourceItemId or weapon definitionId producing
  // gas-typed damage should be listed so notifyEnemyGasTick fires.
]);

/** Returns true if the source is a gas damage source — caller-side check. */
export function isGasSource(sourceId: string | undefined | null): boolean {
  if (!sourceId) return false;
  return GAS_SOURCE_IDS.has(sourceId);
}

const GAS_SPRITE_URL = '/effects/gas_cloud.png';
const GAS_SPRITE_URL_ALT = '/effects/gas_cloud_alt.png';

export function getGasOverlayUrls(): string[] {
  return [GAS_SPRITE_URL, GAS_SPRITE_URL_ALT];
}

interface GasState {
  enemyId: number;
  lastTickTime: number;       // game-time of latest tick
  totalDamage: number;        // accumulated damage this gas window
  ticksReceived: number;
  // Anim seed: which sprite variant + per-enemy phase offset so different
  // enemies don't pulse in lockstep.
  spriteVariant: 0 | 1;
  phaseSeed: number;          // 0..(2π)
}

const _gasStates: Map<number, GasState> = new Map();

let _currentTime = 0;

/**
 * Engine plumbs floating damage-number push fn (avoids circular import
 * with weaponSystem). Called when a gas tick hits.
 */
type DamageNumberPusher = (x: number, y: number, value: number, isCrit: boolean) => void;
let _damageNumberPusher: DamageNumberPusher | null = null;
export function registerGasDamageNumberPusher(p: DamageNumberPusher | null): void {
  _damageNumberPusher = p;
}

/**
 * Called by enemyAdapter for every gas-tagged damage tick that lands on
 * an enemy. Records the state + spawns a floating damage number above
 * the enemy via the registered pusher.
 */
export function notifyEnemyGasTick(
  enemyId: number,
  enemyX: number,
  enemyY: number,
  damage: number,
): void {
  let s = _gasStates.get(enemyId);
  if (!s) {
    s = {
      enemyId,
      lastTickTime: _currentTime,
      totalDamage: 0,
      ticksReceived: 0,
      spriteVariant: (Math.random() < 0.5 ? 0 : 1) as 0 | 1,
      phaseSeed: Math.random() * Math.PI * 2,
    };
    _gasStates.set(enemyId, s);
  }
  s.lastTickTime = _currentTime;
  s.totalDamage += damage;
  s.ticksReceived++;
  // Float up "GAS +N" indicator. Dylan: "i need text to be avoe to
  // show the gas damage or slow or whatever. if the enemy it getting
  // gas dammage for many seconds it needs to tick that gas dammage
  // over the enemy over and over in real time of the effect."
  if (_damageNumberPusher) {
    try {
      _damageNumberPusher(enemyX, enemyY - 12, Math.round(damage), false);
    } catch { /* never crash on tick */ }
  }
  void GAS_TEXT_FLOAT_DURATION;
}

/** Per-frame tick. Engine calls once at top of updateGame. */
export function tickGasOverlay(time: number): void {
  _currentTime = time;
  // Cleanup expired states (no tick for GAS_FADE_TIME_SEC). Use Array.from
  // to avoid the TS downlevelIteration warning on Map iteration.
  for (const [id, s] of Array.from(_gasStates.entries())) {
    if (time - s.lastTickTime > GAS_FADE_TIME_SEC) {
      _gasStates.delete(id);
    }
  }
}

/** Reset on initGame. */
export function clearGasOverlay(): void {
  _gasStates.clear();
}

/**
 * Drop a state when an enemy dies (so the overlay doesn't linger on
 * the corpse). Called from enemySystem.ts on death.
 */
export function clearGasStateForEnemy(enemyId: number): void {
  _gasStates.delete(enemyId);
}

interface EnemyView {
  id: number;
  x: number;
  y: number;
  hitboxRadius: number;
  spriteSize: number;
  alive: boolean;
}

/**
 * Render the gas cloud overlay for every gassed enemy. Called by engine
 * AFTER enemiesModule.render (so the cloud sits above enemies, per
 * Dylan: "live over enemies"), but BEFORE player + weapons + projectiles
 * (so the cloud is under the player). See module-level docblock for
 * layer ordering.
 *
 * Overlay size = enemy.hitboxRadius × 2.4 — slightly bigger than the
 * hitbox for a "puff around the enemy" feel rather than tightly hugging
 * the body. Dylan's spec was "size of their hit box" — 2.4× radius
 * matches that read while leaving room for the soft cloud edges.
 */
export function renderGasOverlayBelow(
  ctx: CanvasRenderingContext2D,
  camera: { x: number; y: number; zoom: number },
  width: number,
  height: number,
  gameTime: number,
  enemies: EnemyView[],
  getCachedImage: (url: string) => HTMLImageElement | null,
): void {
  if (_gasStates.size === 0) return;
  const baseImg = getCachedImage(GAS_SPRITE_URL);
  const altImg = getCachedImage(GAS_SPRITE_URL_ALT);
  if ((!baseImg || !baseImg.complete) && (!altImg || !altImg.complete)) return;

  const enemyById = new Map<number, EnemyView>();
  for (const e of enemies) enemyById.set(e.id, e);

  for (const [id, s] of Array.from(_gasStates.entries())) {
    const e = enemyById.get(id);
    if (!e || !e.alive) continue;

    const elapsed = gameTime - s.lastTickTime;
    if (elapsed > GAS_FADE_TIME_SEC) continue;

    // Fade alpha = 25% × (1 - elapsed / fadeTime). At elapsed=0 → 25%.
    // At elapsed=fadeTime → 0%.
    const fadeAlpha =
      GAS_OVERLAY_OPACITY * Math.max(0, 1 - elapsed / GAS_FADE_TIME_SEC);

    const screenX = (e.x - camera.x) * camera.zoom + width / 2;
    const screenY = (e.y - camera.y) * camera.zoom + height / 2;

    // Size based on hitbox: 2.4× radius gives a "cloud puff around the
    // enemy" read. Falls back to spriteSize for enemies whose hitbox
    // radius isn't set.
    const baseSize = (e.hitboxRadius > 0 ? e.hitboxRadius * 2.4 : e.spriteSize);
    const size = baseSize * camera.zoom;

    if (
      screenX < -size || screenX > width + size ||
      screenY < -size || screenY > height + size
    ) continue;

    const img = (s.spriteVariant === 0 ? baseImg : altImg) ?? baseImg ?? altImg;
    if (!img || !img.complete || img.naturalWidth === 0) continue;

    // Subtle pulse on the alpha + size to sell the "active gas" feel
    // without distracting from gameplay. Per-enemy phase seed prevents
    // synchronized pulsing across the room.
    const pulse = 1 + 0.06 * Math.sin(gameTime * 4 + s.phaseSeed);
    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    ctx.imageSmoothingEnabled = false;
    const drawSize = size * pulse;
    ctx.drawImage(
      img,
      screenX - drawSize / 2,
      screenY - drawSize / 2,
      drawSize,
      drawSize,
    );
    ctx.restore();
  }
}

/**
 * Reserved top-pass render hook. Currently unused — see module docblock.
 * Stubbed so the engine call site stays stable if we add a top-pass
 * later (e.g. boss-specific gas emphasis).
 */
export function renderGasOverlayAbove(
  _ctx: CanvasRenderingContext2D,
  _camera: { x: number; y: number; zoom: number },
  _width: number,
  _height: number,
  _gameTime: number,
  _enemies: EnemyView[],
  _getCachedImage: (url: string) => HTMLImageElement | null,
): void {
  // intentionally empty
}
